import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { AppConstants } from '../../../utils/AppConstants';
import { ReportTemplateVar } from '../../../models/ReportTemplate';
import { DefaultReportTemplate } from '../../../models/DefaultReportTemplate';
import { ReportTemplateService } from '../../../services/report-template.service';
import { DefaultReportTemplateV2Service } from '../../../services/default-report-template-v2.service';
import { AppEnums, EnumTypeTranslator } from '../../../models/AppEnums';
import { AlertService } from '../../shared/alert/alert.service';
import { ReportEditor } from '../../reports/shared/components/report-editor/report-editor';
import { applyExampleValues } from '../../../utils/report-preview';

const HEADER_TYPE = 'HEADER';

@Component({
  selector: 'app-default-report-template-v2-detail',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    FormsModule,
    ReportEditor],
  templateUrl: './default-report-template-v2-detail.component.html',
  styleUrl: './default-report-template-v2-detail.component.scss',
})
export class DefaultReportTemplateV2DetailComponent implements OnInit {

  type?: string;
  defaultReportTemplate: DefaultReportTemplate = { innerHtml: '', headerHtml: '' };
  detail_title: string = 'Nuovo template default v2';

  // Se false il template usa l'intestazione condivisa (default di tipo HEADER)
  customHeader: boolean = false;
  sharedHeaderHtml: string = '';

  previewHTML!: SafeHtml;

  reportTemplateVariables: ReportTemplateVar[] = [];
  reportTemplateTypes: EnumTypeTranslator[] = AppEnums.reportTemplateTypes;

  constructor(
    private sanitizer: DomSanitizer,
    private reportTemplateService: ReportTemplateService,
    private defaultReportTemplateService: DefaultReportTemplateV2Service,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService,
    private router: Router,
    private activatedRoute: ActivatedRoute) {
    fullSpinnerService.spinner(undefined);
  }

  get isHeaderType(): boolean {
    return this.defaultReportTemplate.type === HEADER_TYPE;
  }

  ngOnInit(): void {
    this.reportTemplateService.findAllVars().subscribe({
      next: (res) => this.reportTemplateVariables = res
    });

    this.defaultReportTemplateService.find({ type: HEADER_TYPE, pageSize: 0, pageNumber: 1 }).subscribe({
      next: (res) => this.sharedHeaderHtml = res.items[0]?.innerHtml ?? ''
    });

    this.type = this.activatedRoute.snapshot.paramMap.get('type') ?? undefined;
    if (this.type) {
      this.fullSpinnerService.spinner('');
      this.defaultReportTemplateService.get(this.type).subscribe({
        next: (res) => {
          this.defaultReportTemplate = res;
          this.defaultReportTemplate.innerHtml = res.innerHtml ?? '';
          this.defaultReportTemplate.headerHtml = res.headerHtml ?? '';
          this.customHeader = this.defaultReportTemplate.headerHtml !== '';
          this.detail_title = res.name!;
        }, complete: () => {
          this.fullSpinnerService.spinner(undefined);
        }
        , error: () => {
          this.fullSpinnerService.spinner(undefined);
        }
      });
    }
    else {
      // In creazione propongo solo le tipologie che non hanno ancora un template default v2
      this.defaultReportTemplateService.find({ pageSize: 0, pageNumber: 1 }).subscribe({
        next: (res) => {
          const usedTypes = res.items.map(item => item.type);
          this.reportTemplateTypes = AppEnums.reportTemplateTypes.filter(type => !usedTypes.includes(type.id));
        }
      });
    }
  }

  updatePreview(): void {
    const header = this.isHeaderType ? '' : (this.customHeader ? this.defaultReportTemplate.headerHtml : this.sharedHeaderHtml);
    const html = (header ?? '') + (this.defaultReportTemplate.innerHtml ?? '');
    this.previewHTML = this.sanitizer.bypassSecurityTrustHtml(applyExampleValues(html, this.reportTemplateVariables));
  }

  onSubmit(form: NgForm): void {
    if (!form.valid) {
      form.control.markAllAsTouched();
      return;
    }
    this.fullSpinnerService.spinner('');
    if (this.isHeaderType || !this.customHeader) {
      this.defaultReportTemplate.headerHtml = '';
    }
    const save$ = this.type
      ? this.defaultReportTemplateService.update(this.type, this.defaultReportTemplate)
      : this.defaultReportTemplateService.create(this.defaultReportTemplate);
    save$.subscribe({
      next: () => {
        this.alertService.addErrors(AppConstants.titleSuccessMessage, [AppConstants.successMessage], true);
        this.router.navigate(['default-report-templates-v2']);
      }, complete: () => {
        this.fullSpinnerService.spinner(undefined);
      }
      , error: () => {
        this.alertService.addErrors(AppConstants.titleErrorMessage, [AppConstants.errorMessage], true);
        this.fullSpinnerService.spinner(undefined);
      }
    });
  }

  back() {
    this.router.navigate(['default-report-templates-v2']);
  }
}
